import React, { useState } from 'react';
import { ArrowLeft, Eye, EyeOff, LockKeyhole, Mail } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const inputClass = 'box-border w-full   rounded-lg border-0 bg-[#06110c] py-3 pl-10 pr-4 text-sm font-medium text-white outline-none ring-1 ring-inset ring-white/10 transition placeholder:text-white/25 hover:ring-white/20 focus:ring-2 focus:ring-[#20c77a]';

const Login = () => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { login, authError } = useAuth();
  const navigate = useNavigate();

  const handleChange = (event) =>
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    const user = await login(form.email, form.password);
    setSubmitting(false);

    if (user) {
      if (user.role === 'client') navigate('/client/dashboard');
      else if (user.role === 'provider') navigate('/provider/dashboard');
      else if (user.role === 'admin') navigate('/admin/dashboard');
    }
  };

  return (
    <main className="min-h-screen w-full overflow-x-hidden bg-[#020906] font-dm text-white">
      <title>Log In | GIGGS</title>

      <div className="grid min-h-screen w-full lg:grid-cols-[minmax(360px,0.82fr)_minmax(0,1.18fr)] xl:grid-cols-[minmax(430px,0.9fr)_minmax(0,1.1fr)]">
        <aside className="relative min-h-[250px] overflow-hidden bg-[#07140e] sm:min-h-[320px] lg:min-h-screen">
          <video
            src="/gif/login-register.mp4"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-label="Local service providers working through Giggs"
            className="absolute inset-0 h-full w-full object-cover opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#04100b]/20 via-[#04100b]/35 to-[#020906]/95 lg:bg-gradient-to-t lg:from-[#020906]/95 lg:via-[#03110b]/45 lg:to-[#04100b]/20" />

          <div className="relative z-10 flex h-full min-h-[250px] flex-col justify-between p-5 sm:min-h-[320px] sm:p-7 lg:min-h-screen lg:p-10 xl:p-12">
            <Link to="/" className="inline-flex w-fit no-underline" aria-label="Giggs home">
              <img src="/images/logo-white.png" alt="Giggs" className="h-8 w-auto sm:h-9 lg:h-10" />
            </Link>

            <div className="max-w-lg pb-1 text-white sm:pb-2 lg:pb-3">
              <span className="inline-flex rounded-full border border-white/10 bg-black/20 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#7dffba] backdrop-blur-sm sm:text-xs">
                Welcome back
              </span>
              <h1 className="mb-0 mt-4 max-w-md font-nunito text-[clamp(1.65rem,4vw,3rem)] font-bold leading-[1.08] tracking-[-0.025em] text-white">
                Manage your Giggs account in one place
              </h1>
              <p className="mb-0 mt-3 hidden max-w-md text-sm leading-6 text-white/60 sm:block lg:text-base">
                Clients can manage bookings and Giggers can manage jobs, availability, services and payouts.
              </p>
            </div>
          </div>
        </aside>

        <section className="relative flex min-w-0 items-center justify-center overflow-hidden px-4 py-8 sm:px-6 sm:py-10 lg:px-[clamp(2rem,5vw,6rem)] lg:py-12">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_70%_10%,rgba(25,177,105,0.13),transparent_34%),linear-gradient(rgba(255,255,255,0.018)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.018)_1px,transparent_1px)] bg-[size:auto,22px_22px,22px_22px]" />

          <div className="relative z-10 w-full max-w-[540px]">
            <div className="mb-5 flex items-center justify-between gap-4 sm:mb-6">
              <Link to="/" className="inline-flex items-center gap-2 text-xs font-semibold text-white/40 no-underline transition hover:text-[#46dc96] sm:text-sm">
                <ArrowLeft size={16} /> Back to home
              </Link>
              <Link to="/" className="lg:hidden" aria-label="Giggs home">
                <img src="/images/logo-white.png" alt="Giggs" className="h-8 w-auto" />
              </Link>
            </div>

            <div className="rounded-2xl border border-[#1c4b36]/70 bg-[#04100b]/95 p-5 shadow-[0_25px_90px_rgba(0,0,0,0.38)] backdrop-blur-sm sm:p-7 lg:p-8">
              <div className="text-center">
                <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#42d993]">Sign in</span>
                <h2 className="mb-0 mt-2 font-nunito text-[clamp(1.8rem,7vw,2.45rem)] font-bold tracking-[-0.02em] text-white">Welcome to Giggs</h2>
                <p className="mx-auto mb-0 mt-2 max-w-md text-sm leading-6 text-white/35">Use the account you created as a client or Gigger.</p>
              </div>

              {authError && <div className="mt-5 rounded-lg border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm font-medium text-red-200">{authError}</div>}

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <label className="block min-w-0">
                  <span className="mb-1.5 block text-[11px] font-semibold text-white/65">Email address</span>
                  <div className="relative">
                    <Mail size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-white/25" />
                    <input className={inputClass} type="email" name="email" placeholder="you@example.com" value={form.email} onChange={handleChange} autoComplete="email" required />
                  </div>
                </label>

                <label className="block min-w-0">
                  <span className="mb-1.5 block text-[11px] font-semibold text-white/65">Password</span>
                  <div className="relative">
                    <LockKeyhole size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-white/25" />
                    <input className={`${inputClass} pr-11`} type={showPassword ? 'text' : 'password'} name="password" placeholder="Enter your password" value={form.password} onChange={handleChange} autoComplete="current-password" required />
                    <button type="button" onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? 'Hide password' : 'Show password'} className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md border-0 bg-transparent text-white/25 transition hover:bg-white/[0.05] hover:text-white/70">
                      {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                </label>

                <div className="flex justify-end">
                  <Link to="/forgot-password" className="text-xs font-semibold text-[#42d993] no-underline transition hover:text-[#71efb2]">Forgot password?</Link>
                </div>

                <button type="submit" disabled={submitting} className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-[#20c77a] bg-[#12ad68] px-5 text-sm font-bold text-[#02140b] shadow-[0_10px_30px_rgba(18,173,104,0.16)] transition hover:-translate-y-0.5 hover:bg-[#25d080] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0">
                  {submitting ? 'Signing in…' : 'Sign in'}
                </button>
              </form>

              <div className="mt-6 border-t border-white/[0.07] pt-5">
                <p className="m-0 text-center text-xs text-white/35">New to Giggs?</p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <Link to="/client-register" className="inline-flex min-h-11 items-center justify-center rounded-lg border border-white/10 bg-white/[0.035] px-4 text-center text-sm font-semibold text-white/70 no-underline transition hover:border-[#20c77a]/45 hover:bg-[#20c77a]/[0.07] hover:text-white">Create client account</Link>
                  <Link to="/provider-register" className="inline-flex min-h-11 items-center justify-center rounded-lg border border-[#20c77a]/70 bg-[#0d6d43] px-4 text-center text-sm font-semibold text-[#7dffba] no-underline transition hover:bg-[#118052]">Become a Gigger</Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Login;
